import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BatteryLow, BatteryMedium, BatteryFull, Brain, X, ChevronRight } from "lucide-react";
import { PsiReceiptCard } from "@/components/PsiReceipts";

const FATIGUE = ["Fresh", "Good", "Okay", "Tired", "Gassed"];
const CONFIDENCE = ["Shaky", "Unsure", "Neutral", "Locked in", "Unstoppable"];

const Scale = ({ label, hint, value, onChange, labels, color, testid }) => (
  <div data-testid={testid}>
    <div className="flex justify-between text-[10px] uppercase tracking-widest mb-1.5">
      <span className="text-zinc-400 font-bold">{label}</span>
      <span className={value ? `${color} font-bold` : "text-zinc-600"}>{value ? labels[value - 1] : hint}</span>
    </div>
    <div className="grid grid-cols-5 gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`${testid}-${n}`} onClick={() => onChange(n)}
          className={`h-9 border font-mono text-sm font-bold transition-all ${value === n ? `border-current ${color} bg-white/10 scale-105` : value && n < value ? `border-current/40 ${color} opacity-60` : "border-pb-border text-zinc-500 hover:border-zinc-400 hover:text-white"}`}>
          {n}
        </button>
      ))}
    </div>
  </div>
);

export function SelfReportForm({ match, areas, onDone, compact = false }) {
  const [fatigue, setFatigue] = useState(0);
  const [confidence, setConfidence] = useState(0);
  const [struggles, setStruggles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setFatigue(0); setConfidence(0); setStruggles([]); setNote(""); }, [match?.ref]);

  const toggle = (k) => setStruggles((s) => s.includes(k) ? s.filter((x) => x !== k) : s.length >= 4 ? s : [...s, k]);
  const send = async (skipped = false) => {
    if (!skipped && (!fatigue || !confidence)) return toast.error("Rate your fatigue and confidence first");
    setBusy(true);
    try {
      const { data } = await api.post("/player/self-reports", { ref: match.ref, fatigue, confidence, struggles, note, skipped });
      if (!skipped) toast.success(data.remaining > 0 ? `Logged — ${data.remaining} more match${data.remaining > 1 ? "es" : ""} to report` : "Logged — feeding your PBPA weakness profile");
      onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save your report"); }
    finally { setBusy(false); }
  };
  const outcome = match.won === true ? "WIN" : match.won === false ? "LOSS" : "MATCH";

  return (
    <div className="space-y-4" data-testid="self-report-form">
      <div className="flex items-center gap-2 text-xs">
        <span className={`font-mono font-black px-1.5 py-0.5 border ${match.won ? "text-emerald-300 border-emerald-500/50" : match.won === false ? "text-pb-red border-pb-red/50" : "text-zinc-300 border-zinc-600"}`} data-testid="self-report-outcome">{outcome}</span>
        <span className="text-zinc-300 truncate">{match.opp ? `vs ${match.opp}` : match.where}</span>
        <span className="text-zinc-600 truncate">· {match.where}</span>
      </div>
      <Scale label="Fatigue" hint="how gassed were you?" value={fatigue} onChange={setFatigue} labels={FATIGUE} color="text-amber-400" testid="self-report-fatigue" />
      <Scale label="Confidence" hint="how did you feel out there?" value={confidence} onChange={setConfidence} labels={CONFIDENCE} color="text-cyan-300" testid="self-report-confidence" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">What gave you trouble? <span className="text-zinc-600 normal-case tracking-normal">(pick up to 4)</span></div>
        <div className="flex flex-wrap gap-1.5" data-testid="self-report-struggles">
          {areas.map((a) => (
            <button key={a.key} type="button" data-testid={`self-report-struggle-${a.key}`} onClick={() => toggle(a.key)}
              className={`text-[11px] px-2.5 py-1 border transition-colors ${struggles.includes(a.key) ? "border-pb-red bg-pb-red/15 text-white" : "border-pb-border text-zinc-400 hover:border-zinc-400 hover:text-white"}`}>
              {a.label}
            </button>
          ))}
        </div>
      </div>
      {!compact && (
        <input data-testid="self-report-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="One line for your future self (optional)…"
          className="w-full bg-pb-elevated border border-pb-border px-3 py-2 text-xs text-white focus:border-cyan-400 outline-none" />
      )}
      <div className="flex items-center gap-2">
        <button data-testid="self-report-submit" disabled={busy} onClick={() => send(false)}
          className="flex-1 bg-cyan-400 text-black font-display font-black uppercase tracking-wider py-2.5 text-sm hover:bg-cyan-300 disabled:opacity-50 transition-colors">
          Log It
        </button>
        <button data-testid="self-report-skip" disabled={busy} onClick={() => send(true)}
          className="text-[11px] uppercase tracking-wider text-zinc-500 hover:text-white px-3 py-2 transition-colors">Skip</button>
      </div>
    </div>
  );
}

export default function PostMatchReport() {
  const { pathname } = useLocation();
  const [pending, setPending] = useState([]);
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(true);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("pb_selfreport_snooze") === "1");

  useEffect(() => {
    if (hidden) return;
    const load = () => api.get("/player/self-reports/pending").then(({ data }) => setPending(data || [])).catch(() => {});
    load();
    api.get("/player/self-reports/areas").then(({ data }) => setAreas(data || [])).catch(() => {});
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [hidden]);

  // never cover the scorekeeper pad or court boards
  if (hidden || pending.length === 0 || areas.length === 0 || /^\/(scorekeeper|td-court-board|court-board|live)/.test(pathname)) return null;
  const m = pending[0];
  const snooze = () => { sessionStorage.setItem("pb_selfreport_snooze", "1"); setHidden(true); };
  const Icon = m.won ? BatteryFull : m.won === false ? BatteryLow : BatteryMedium;

  return (
    <div className="fixed bottom-4 right-4 z-[70] w-[min(92vw,380px)]" data-testid="post-match-report">
      {!open ? (
        <button data-testid="post-match-report-open" onClick={() => setOpen(true)}
          className="w-full flex items-center gap-2 border border-cyan-400/60 bg-pb-surface/95 backdrop-blur px-4 py-3 text-left shadow-[0_0_24px_rgba(34,211,238,0.25)] hover:border-cyan-300 transition-colors">
          <Brain className="w-4 h-4 text-cyan-300 animate-pulse" />
          <span className="flex-1 text-xs"><span className="font-bold text-white">Post-match check-in</span> <span className="text-zinc-400">· {pending.length} match{pending.length > 1 ? "es" : ""} to report</span></span>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </button>
      ) : (
        <div className="border border-cyan-400/60 bg-pb-surface/95 backdrop-blur p-4 shadow-[0_0_32px_rgba(34,211,238,0.25)] animate-in slide-in-from-bottom-4 max-h-[85vh] overflow-y-auto">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div>
              <p className="font-mono text-cyan-300 uppercase tracking-[0.3em] text-[10px] flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> PBPA Post-Match Check-in</p>
              <p className="text-sm font-display font-black uppercase text-white">How did that one feel?</p>
              <p className="text-[10px] text-zinc-500">30 seconds · sharpens your weakness detection{pending.length > 1 ? ` · ${pending.length} to go` : ""}</p>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="post-match-report-minimize" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white text-xs px-1" title="Minimize">—</button>
              <button data-testid="post-match-report-snooze" onClick={snooze} className="text-zinc-500 hover:text-white" title="Not now"><X className="w-4 h-4" /></button>
            </div>
          </div>
          <PsiReceiptCard ev={m} compact testid="post-match-psi-receipt" />
          <div className="mt-3" />
          <SelfReportForm match={m} areas={areas} compact onDone={() => setPending((p) => p.filter((x) => x.ref !== m.ref))} />
        </div>
      )}
    </div>
  );
}
